import React, { useContext, useEffect } from "react"
import {Button, ButtonProps, Text} from "../../vanilla"
import {Nullable} from "../../types/utils"
import {Hit, Promotion} from "../../store/ProductStore"
import {getDisabledButtonText, isProductPurchaseDisabled} from "../../utils/utils"
import {useBasketStore, useCustomerStore, useRapidDeliveryStore} from '../../store/hooks/useStore'
import {useLocation} from 'react-router-dom'
import {redirectIfNotRegistered} from "../../utils/login-redirect-utils"
import useNavigation from '../../hooks/use-navigation'
import { useAddToCartTracking } from "../../analytics/click-event-tracking"
import { PartialProductItem } from "../../types/store/basket"
import {PeekCarouselContext} from '../../contexts/peek-carousel-context'

interface AddToBasketButtonProps extends ButtonProps {
    hit: Hit,
    displayPrice: Nullable<number>
    isPLPTile?: boolean
    isPDPPage?: boolean
    hitItemIndex?: number
    promotion?: Promotion
    ATBEventDetails?: {
        locationOfCart?: string
        viewingCategoryId?: string
        searchQuery?: string
    }
    isSearch?: boolean
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
    navigate?: ReturnType<typeof useNavigation>
}

export const ATBButton = React.forwardRef((props: AddToBasketButtonProps, ref: React.Ref<HTMLButtonElement>) => {
    const {
        hit,
        displayPrice,
        isPLPTile,
        isPDPPage,
        hitItemIndex,
        isSearch = false,
        ATBEventDetails,
        onClick,
        promotion,
        navigate: navigationProp,
        ...rest
    } = props

    const isInPeekCarousel = useContext(PeekCarouselContext)

    // Re-direct if customer is not logged in
    let navigate = useNavigation()
    navigate = navigationProp ? navigationProp : navigate
    const location = useLocation()
    const pathname = `${location.pathname}${location.search}${location.hash}`
    const {isRegistered, loading: loadingCustomer} = useCustomerStore()
    const basketStore = useBasketStore()
    const rapidDeliveryStore = useRapidDeliveryStore()
    const isExcludedFromRapidDelivery = !!hit.excludeFromRapidDelivery && rapidDeliveryStore.isRapidDeliveryBooked
    const isButtonDisabled = isProductPurchaseDisabled(!!displayPrice, hit.availableForDayAtStore, hit.isAddToCartDisabled, isExcludedFromRapidDelivery)
    const disabledButtonText = getDisabledButtonText(hit.disabledStatus)
    const sendAddToCartData = useAddToCartTracking()
    const { locationOfCart, viewingCategoryId, searchQuery } = ATBEventDetails || {}
    const ATBPayload = {
        hit,
        cartLocation: locationOfCart || '',
        viewingCategoryId: viewingCategoryId || '',
        searchQuery: searchQuery || '',
        hitItemIndex
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (basketStore.basketLimitThreshold?.maxReached) {
            rapidDeliveryStore.updateRapidBasketCapacityAlert(basketStore.count)
            return
        }
        if (!isRegistered && !loadingCustomer) {
            basketStore.setPendingATBEvent(ATBPayload)
            basketStore.setPendingProducts([{ product: { id: hit.id }, quantity: 1 }] as PartialProductItem[])
            basketStore.setLocationOfCart(locationOfCart ?? null)
            basketStore.setPendingPromoModal(promotion)
        }
        if (!redirectIfNotRegistered({navigate, pathname, isRegistered, loadingCustomer}) && onClick) {
            onClick(event)
        } else {
            basketStore.setPendingATBEvent(ATBPayload)
            basketStore.setPendingProducts([{ product: { id: hit.id }, quantity: 1 }] as PartialProductItem[])
            basketStore.setLocationOfCart(locationOfCart ?? null)
        }
    }

    useEffect(() => {
        if (basketStore.pendingATBEvent && !basketStore.openProductUnavailableModal.open) {
            const { hit, cartLocation, viewingCategoryId, searchQuery, hitItemIndex } = basketStore.pendingATBEvent
            const addToCartData = {
                product: hit,
                cartLocation,
                viewingCategoryId,
                searchQuery,
                hitItemIndex
            }
            sendAddToCartData(addToCartData)
            basketStore.setPendingATBEvent(null)
        }
    }, [basketStore.pendingATBEvent]
)

    return (
        <Button
        variant={!isButtonDisabled ? "primary" : "secondary"}
        disabled={isButtonDisabled}
        size={(isPDPPage || isPLPTile) ? "smWide" : "sm"}
        paddingY={(isPDPPage || isPLPTile) ? "8px" : "0px"}
        data-test-selector="add-to-basket-button"
        onClick={handleClick}
        {...rest}
        ref={ref}>
            {isSearch
                ? <Text variant="unstyled" fontSize="sm" display='block'>
                        {!isButtonDisabled ? 'Add' : disabledButtonText}
                    </Text>
                :  <>
                    <Text variant="unstyled" fontSize={isPDPPage ? "sm" : "inherit"} display={isPLPTile && !isInPeekCarousel ? ['none', 'block'] : 'block'}>{!isButtonDisabled ? 'Add to Basket' : disabledButtonText}</Text>
                    <Text variant="unstyled" fontSize={isPLPTile ? "sm" : "inherit"} display={isPLPTile && !isInPeekCarousel ? ['block', 'none'] : 'none'}>
                        {!isButtonDisabled ? 'Add' : disabledButtonText}
                    </Text>
                </>
            }
        </Button>
    )
})
